/**
 * PatientUpdateToast — soft bottom-right toast that surfaces ONE pending
 * "patient came back" simulated update per page visit. Wraps any page
 * (mounted at App level). Auto-dismisses on click or after 18s. Marks
 * the update as seen in localStorage so we don't repeat.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { peekNextUnseen, markSeen } from "../lib/patientUpdates";

const TONE = {
  good: "border-[#6B8F71]/50 bg-[#6B8F71]/10",
  info: "border-[#6B8F71]/50 bg-[#6B8F71]/10",
  warn: "border-[#D96C5B]/60 bg-[#D96C5B]/10",
};

export default function PatientUpdateToast() {
  const [update, setUpdate] = useState(null);

  useEffect(() => {
    // Short delay so the toast lands AFTER the page paints — feels less abrupt.
    const t = setTimeout(() => setUpdate(peekNextUnseen()), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!update) return;
    const t = setTimeout(() => dismiss(), 18000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update]);

  function dismiss() {
    if (update) markSeen(update.id);
    setUpdate(null);
  }

  return (
    <AnimatePresence>
      {update && (
        <motion.div
          initial={{ opacity: 0, x: 20, y: 8 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: 20, y: 8 }}
          transition={{ duration: 0.25 }}
          className="fixed bottom-6 right-6 z-50 max-w-sm"
          data-testid="patient-update-toast"
        >
          <div className={`relative glass-card rounded-2xl border p-4 pr-8 shadow-2xl ${TONE[update.tone] || TONE.info}`}>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss"
              data-testid="patient-update-dismiss"
              className="absolute top-2 right-2 text-white/50 hover:text-white text-xs"
            >
              ×
            </button>
            <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#6B8F71] mb-1.5">
              Patient update · {update.patient}
            </p>
            <p className="text-sm text-white/90 leading-relaxed">{update.body}</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
